import { AxiosError } from 'axios';
import { put, takeLatest } from 'redux-saga/effects';

import { CastCrewPerson } from 'store/railsBuilder/reducer.types';

import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import { isAssetVod } from 'utils/checkVodType';
import { ERROR_CODE_A012, ERROR_CODE_A014 } from 'utils/error/errorCodes';

import { DetailsRequestUrlType } from 'screens/Detail/types';

import {
    getDetails,
    getDetailsFailure,
    getDetailsSuccess,
    setDetailsEpisode,
    setDetailsEpisodeCastAndCrew,
} from './actions';
import { GET_DETAILS, SET_DETAILS_EPISODE } from './constants';
import loadBTVProgramData, {
    LoadBTVProgramDataReturnType,
} from './utils/loadBTVProgramData';
import loadCastAndCrewData from './utils/loadCastAndCrewData';
import loadVODData, { LoadVODDataReturnType } from './utils/loadVODData';

function* onGetDetails({ payload, meta }: ReturnType<typeof getDetails>) {
    try {
        const { id, templateId, playbackType, filterType, channelId } = payload;
        const { autoRetry, retryCount, retryDuration, abortController } = meta;
        const isVod = isAssetVod(playbackType);

        if (filterType === DetailsRequestUrlType.PROGRAM && !isVod) {
            const details: LoadBTVProgramDataReturnType =
                yield loadBTVProgramData({
                    channelId,
                    id,
                    autoRetry,
                    retryCount,
                    retryDuration,
                    abortController,
                });

            if (!details) {
                throw new Error('Was not been able to load BTV program data');
            }

            const castAndCrew: CastCrewPerson[] = details
                ? yield loadCastAndCrewData(details)
                : [];

            yield put(
                getDetailsSuccess({
                    details,
                    castAndCrew,
                    templateId,
                }),
            );
        } else if (filterType === DetailsRequestUrlType.VOD || isVod) {
            const details: LoadVODDataReturnType = yield loadVODData({
                id,
            });

            if (!details) {
                throw new Error('Was not been able to load VOD editorial data');
            }

            const castAndCrew: CastCrewPerson[] = details
                ? yield loadCastAndCrewData(details)
                : [];

            yield put(
                getDetailsSuccess({
                    details,
                    castAndCrew,
                    templateId,
                }),
            );
        }
    } catch (error: any) {
        const maybeAxiosError = error as AxiosError;
        let errorObject = {
            message: error.message,
            code: ERROR_CODE_A012,
            templateId: payload.templateId,
        };

        if (isErrorNeedsRetry(maybeAxiosError)) {
            errorObject = {
                message: error.message,
                code: ERROR_CODE_A014,
                templateId: payload.templateId,
            };
        }

        yield put(getDetailsFailure(errorObject));
    }
}

function* onSetDetailsEpisode({
    payload: { episode, templateId },
}: ReturnType<typeof setDetailsEpisode>) {
    const castAndCrew: CastCrewPerson[] = yield loadCastAndCrewData(episode);

    yield put(setDetailsEpisodeCastAndCrew(templateId, castAndCrew));
}

export default [
    takeLatest(GET_DETAILS, onGetDetails),
    takeLatest(SET_DETAILS_EPISODE, onSetDetailsEpisode),
];
